import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { IGridPosition, IUser, IUserConfig } from '../interfaces/interfaces';
import { UserConfigService } from '../services/user-config.service';

const USER_STORAGE_KEY = 'stencasUserData';
const CONFIG_STORAGE_KEY = 'stencasUserConfig';

@Injectable({
  providedIn: 'root',
})
export class UserStore {
  private readonly document = inject(DOCUMENT);
  private readonly userConfigService = inject(UserConfigService);

  // Signals
  private readonly userData = signal<IUser | undefined>(undefined);
  readonly displayType = signal<'card' | 'list'>('card');
  readonly darkMode = signal<boolean>(false);
  readonly gridPosition = signal<Record<string, IGridPosition>>({});
  // Round-tripped through the cache and the PUT body so the server keeps
  // whatever it put in `extra` - nothing in the app reads it yet.
  private readonly configExtra = signal<Record<string, unknown> | undefined>(
    undefined,
  );

  // Computed signals (can be used directly in templates)
  readonly isLoggedIn = computed(() => !!this.userData());

  constructor() {
    // Load cached user from localStorage when store is created
    this.loadFromStorage();

    // Hydrate theme/layout from the last cached config synchronously, so the
    // first render already matches the user's settings instead of flashing
    // defaults (light theme, unplaced grid) until GET /me/config lands.
    this.loadConfigFromStorage();

    // displayType/darkMode/gridPosition now live on the BE - fetch them
    // as soon as we know who the user is. Deferred to a microtask so this
    // constructor finishes first: loadConfig() triggers an HTTP call, which
    // lazily builds the interceptor chain, and AuthInterceptor injects
    // UserStore too - calling it synchronously here re-enters UserStore's
    // own (still in-progress) construction and trips Angular's NG0200
    // circular-dependency guard.
    if (this.isLoggedIn()) {
      queueMicrotask(() => this.loadConfig());
    }

    // Auto-save whenever authToken or userData changes
    effect(() => {
      this.saveToStorage();
    });

    // Mirror the config to localStorage on every change - covers both the
    // BE-driven applyConfig() and the local toggleDarkMode() /
    // setDisplayType() / setGridPosition() mutations.
    effect(() => {
      this.saveConfigToStorage();
    });

    // Keep the root element class in sync with the dark mode signal.
    effect(() => {
      this.document.documentElement.classList.toggle('dark', this.darkMode());
    });
  }

  private loadFromStorage() {
    const rawUser = localStorage.getItem(USER_STORAGE_KEY);

    if (rawUser) {
      try {
        const parsed: IUser = JSON.parse(rawUser);
        this.userData.set(parsed);
      } catch {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
  }

  private loadConfigFromStorage() {
    const raw = localStorage.getItem(CONFIG_STORAGE_KEY);

    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<IUserConfig>;

      if (typeof parsed.darkMode === 'boolean') {
        this.darkMode.set(parsed.darkMode);
      }
      if (parsed.displayType === 'card' || parsed.displayType === 'list') {
        this.displayType.set(parsed.displayType);
      }
      if (parsed.gridPosition && typeof parsed.gridPosition === 'object') {
        this.gridPosition.set(parsed.gridPosition);
      }
      if (parsed.extra && typeof parsed.extra === 'object') {
        this.configExtra.set(parsed.extra);
      }
    } catch {
      localStorage.removeItem(CONFIG_STORAGE_KEY);
    }
  }

  private loadConfig() {
    this.userConfigService.getConfig().subscribe({
      next: (config) => this.applyConfig(config),
    });
  }

  private applyConfig(config: IUserConfig) {
    const nextDisplay = config.displayType ?? this.displayType();
    const nextDark = config.darkMode ?? this.darkMode();
    const nextGrid = config.gridPosition ?? this.gridPosition();
    const nextExtra = config.extra ?? this.configExtra();

    // Set each slice only when it actually changed, so a BE response that
    // matches the cache is a no-op: no re-persist, no theme flip, and no new
    // gridPosition object reference to make the grid re-render.
    if (nextDisplay !== this.displayType()) {
      this.displayType.set(nextDisplay);
    }
    if (nextDark !== this.darkMode()) {
      this.darkMode.set(nextDark);
    }
    if (stableStringify(nextGrid) !== stableStringify(this.gridPosition())) {
      this.gridPosition.set(nextGrid);
    }
    if (stableStringify(nextExtra) !== stableStringify(this.configExtra())) {
      this.configExtra.set(nextExtra);
    }
  }

  private persistConfig() {
    this.userConfigService.updateConfig(this.currentConfig()).subscribe();
  }

  private currentConfig(): IUserConfig {
    const config: IUserConfig = {
      darkMode: this.darkMode(),
      displayType: this.displayType(),
      gridPosition: this.gridPosition(),
    };
    const extra = this.configExtra();
    if (extra !== undefined) {
      config.extra = extra;
    }
    return config;
  }

  private resetConfig() {
    this.darkMode.set(false);
    this.displayType.set('card');
    this.gridPosition.set({});
    this.configExtra.set(undefined);
  }

  setDisplayType(type: 'card' | 'list') {
    this.displayType.set(type);
    this.persistConfig();
  }

  toggleDarkMode() {
    this.darkMode.set(!this.darkMode());
    this.persistConfig();
  }

  setGridPosition(gridPosition: Record<string, IGridPosition>) {
    this.gridPosition.set(gridPosition);
    this.persistConfig();
  }

  setUser(user: IUser) {
    // A different user on this device must not inherit the previous user's
    // cached theme/grid. The common same-user reload keeps them, so the
    // hydrated-from-cache values don't flash back to defaults before
    // loadConfig() confirms them.
    if (this.userData()?.id !== user.id) {
      this.resetConfig();
    }
    this.userData.set(user);
    this.loadConfig();
  }

  get user(): IUser | undefined {
    return this.userData();
  }

  private saveToStorage() {
    if (this.userData()) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.userData()));
    }
  }

  private saveConfigToStorage() {
    // Read every signal up front so this effect's dependency set is the same
    // regardless of login state - only the write is gated.
    const serialized = JSON.stringify(this.currentConfig());

    if (!this.isLoggedIn()) {
      return;
    }
    if (serialized === localStorage.getItem(CONFIG_STORAGE_KEY)) {
      return;
    }
    localStorage.setItem(CONFIG_STORAGE_KEY, serialized);
  }

  clearAuth() {
    // Null userData first so isLoggedIn() is already false when the config
    // persist effect flushes - it then early-returns instead of writing the
    // freshly-cleared defaults straight back to storage.
    this.userData.set(undefined);
    this.resetConfig();
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(CONFIG_STORAGE_KEY);
  }
}

// Stable JSON: object keys sorted recursively, so a gridPosition map returned
// by the BE in a different key order than the cache still compares equal.
function stableStringify(value: unknown): string {
  return (
    JSON.stringify(value, (_key, val) =>
      val && typeof val === 'object' && !Array.isArray(val)
        ? Object.fromEntries(
            Object.entries(val as Record<string, unknown>).sort(([a], [b]) =>
              a.localeCompare(b),
            ),
          )
        : val,
    ) ?? ''
  );
}
